import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Button,
  IconInfoFull,
  IconPinSolid,
  MaskedInput,
  Panel,
  Popover,
  PopoverContent,
  ProgressIndicator,
  Select,
  SelectHandler,
  SelectOption,
  Switch,
} from '@ftdr/blueprint-components-react';
import { CityStates, DwellingType } from '@apis/models';
import { classNames } from '@utils';
import { SELLERS_COVERAGE } from '@constants/formField-constants';
import { PlanPriceSummary } from '@components/card/CardPlanPriceSummary';
import { GetIncludedCoveragesHandler } from '@components/card/PlanCard';
import getBrandConstants from '@constants/brands';
import { getBrand } from '@helpers/brand.utils';
import { fireGAEvent } from '@app/core/tracking.service';
import {
  PLAN_PRICE__SHARE_QUOTE,
  PLAN_PRICE__START_ORDER,
  PLAN_PRICE__UPDATE_PRICE,
} from '@constants/ga-events.constants';
import { PlansAndPricesMetaData } from '@apis/models/zesty.api.model';
import { ContentBoxContainer } from '@components/layout/ContentBoxContainer';
import { OptionalCoverageItem } from '@components/card/CardPlanPriceCoverage';
import CardQuoteSummary, { QuoteSummarySelection } from '@components/misc/QuoteSummary';
import ServiceUnavailable from '@components/modal/subcomponents/ServiceUnavailable';
import DrawerPlanPriceShareQuote, {
  DrawerPlanPriceShareQuoteProps,
} from '@components/drawer/DrawerPlanPriceShareQuote';
import DrawerPlanPriceSaveQuote from '@components/drawer/DrawerPlanPriceSaveQuote';
import PlansPriceTradeServiceFeeFooter from '@components/misc/PlansPriceTradeServiceFeeFooter';
import { dwellingTypeDictionary, RESIDENCE_DISPLAY } from '@constants/dictionaries';
import { useNavigate } from 'react-router-dom';
import Path from '@constants/paths';
import { getSquareFootageRangeValue, isAgeNewConstruction } from '@services/helpers';
import { NewOrderRouteData } from '@pages/order/NewOrder';
import { NewOrderData } from '@helpers/new-order-loader';
import { PlansCoverageValues } from '@components/card/CardNewOrderPlansCoverage';
import { hasGuestUnitCoverage } from '@helpers/order.utils';
import REText from '@components/wrappedBDS/REText';
import {
  ProductComparisonCardsMicroFrontend,
  Theme,
  Tenant,
} from '@ftdr/product-comparison-cards-micro-frontend';
import '@ftdr/product-comparison-cards-micro-frontend/dist/assets/css/product-comparison-cards-micro-frontend.css';
import '@ftdr/product-add-on-selection-list-micro-frontend/dist/assets/css/product-add-on-selection-list-micro-frontend.css';
import { ProductAddOnSelectionListMicroFrontend } from '@ftdr/product-add-on-selection-list-micro-frontend';
import useGlobalAlert from '@app/core/GlobalAlertModal';
import { isProdEnv } from '@helpers/utils';
import PlanCardsOld from '@components/misc/PlanCardsOld';
import {
  useOptimizelyFE,
  FEFeatures,
  getTradeServiceFeeVariables,
  TradeServiceFeeVariables,
} from '@context/OptimizelyContext/fe-index';
import ProductAddOnOld from '@components/misc/ProductAddOnOld';
import { cloneDeep } from 'lodash';
import { getCurrentThemeConfig } from '@app/core/theme-config';
import TradeServiceFee from '@components/input/TradeServiceFee';
export const NEW_ORDER_SOURCE_PLANS_AND_PRICES = 'plans-and-prices';

export const PLANS_AND_PRICES_LABELS = {
  EMPTY_RESULT: 'No Plans Found',

  INIT_RESULT_HEADER: 'Enter a ZIP Code',
  INIT_RESULT_SUBTEXT: 'To see plans and prices for that location.',

  NO_RESULT_HEADER: 'Sorry, We Don’t Service That Area',
  NO_RESULT_SUBTEXT: `American Home Shield is unavailable in this area. To request a real estate product, you may call and speak to an Associate regarding other options for your client.
  Please call ${getBrandConstants('AHS').supportPhone}.`,

  FORM_HEADER: 'Base Coverage',
  COVERAGE_SELECTION_HEADER: 'Optional Coverage',
  QUOTE_SUMMARY_HEADER: 'Quote Summary',

  EMPTY_TRADE_SERVICE_FEE: ' ---',

  SUBMIT_BTN: 'Update Price',
  RESIDENCE_TYPE_INPUT: 'Residence Type',
  ZIP_INPUT: 'Zip Code',
  SELLERS_COVERAGE_INPUT: 'Seller Coverage',
  SELLERS_COVERAGE_TOOLTIP: SELLERS_COVERAGE,
  SELLERS_COVERAGE_RESTRICTED_TOOLTIP:
    'Seller’s Coverage is not offered for this type of residence.',

  SERVICE_FEE: 'Trade Service Call Fee',
  SERVICE_FEE_ACRONYM: 'TSCF',
  SERVICE_FEE_HELP: 'Learn More',
  ZIPCODE: 'Zip code affects the price',
  HBW210_SERVICE_FEE: 'Service Call Fee',
};

const PLANS_AND_PRICES_FORM_ERROR = {
  MISSING_RESIDENCE_TYPE: 'Selection required',
  INVALID_ZIP_CODE: 'Enter a valid 5-Digit ZIP code',
};

interface OwnPlansAndPricesTemplateProps {
  /** initial load of the form data */
  initialFormData: PlansAndPricesFormData;
  /** copy of the form data used during submission, used for reference and arguments */
  formData: PlansAndPricesFormData;
  cityOptions: CityStates[];
  loadingPageMeta: boolean;
  loading: boolean;
  loadingPrice: boolean;
  plans: PlanPriceSummary[];
  optionalCoverages: OptionalCoverageItem[];
  selectedPlan: PlanPriceSummary;
  setSelectedPlan: any;
  selectedCoverages: OptionalCoverageItem[];
  quoteSummary: QuoteSummarySelection;
  onPlanSelection: PlanSelectionHandler;
  onServiceFeeChange: (serviceFee: number) => void;
  onCoverageSelection: CoverageSelectionHandler;
  onCoverageSelectionWithQuantity: CoverageSelectionHandlerWithQuantity;
  setSelectedCoverageItems: any;
  tradeServiceFee: number;
  onFormDataChange?: PlansAndPricesFormHandler;
  onSubmit: PlansAndPricesFormHandler;
  /** for a given zip string, return the state of the ZIP */
  getZipCityState: GetZipCityStateHandler;
  autoSubmitForm: boolean;
  meta: PlansAndPricesMetaData;
  getIncludedCoverages: GetIncludedCoveragesHandler;
  loadingFilters: boolean;
  disableSellersCoverageOption: boolean;
}

export type PlansAndPricesTemplateProps = OwnPlansAndPricesTemplateProps &
  Pick<DrawerPlanPriceShareQuoteProps, 'productPricingDetailsRequest'>;

export interface PlansAndPricesFormData {
  residenceType: DwellingType;
  zipCode: string;
  includeSellersCoverage: boolean;
}

export type PlanSelectionHandler = (plan: PlanPriceSummary) => void;
export type CoverageSelectionHandler = (cvg: OptionalCoverageItem) => void;
export type CoverageSelectionHandlerWithQuantity = (cvg: OptionalCoverageItem, qty: number) => void;
export type GetZipCityStateHandler = (
  zip: string,
) => Promise<{ city: string; state: string; cityStates: CityStates[] }>;
export type PlansAndPricesFormHandler = (data: PlansAndPricesFormData) => void;

export interface PlanCardMetaFields {
  planSubtext: string;
  coverageItems: string[];
}

export type PlanCardMetaFieldRecordsByLookup = Record<string, PlanCardMetaFields>;

type ProductCoverage = {
  quantity?: number;
  productCoverageID: string;
  promoted?: boolean;
  price?: number;
};

// Helper function to generate normalized coverage state string for comparison
const generateCoverageStateString = (state: ProductCoverage[]): string => {
  if (!state) return '';
  
  return JSON.stringify(
    state
      .map((item) => ({
        id: item.productCoverageID,
        qty: item.quantity || 0,
      }))
      .sort((a, b) => a.id.localeCompare(b.id))
  );
};

export function extractSortedServiceFees(
  plans: PlanPriceSummary[] | undefined,
  tradeServiceFeeVariables: TradeServiceFeeVariables | undefined,
): string[] {
  if (!tradeServiceFeeVariables?.enabled) return [];
  if (plans?.length > 0) {
    return Array.from(new Set(plans.map((p) => p.serviceFee.toString()))).sort(
      (a, b) => Number(a) - Number(b),
    );
  }
  if (tradeServiceFeeVariables?.available_service_fees) {
    return tradeServiceFeeVariables.available_service_fees
      .split(',')
      .map((item: string) => item.trim());
  }
  return [];
}

export const PlansAndPricesTemplate: React.FC<PlansAndPricesTemplateProps> = (props) => {
  const navigate = useNavigate();

  const brand = getBrand();

  const [init, setInit] = useState<boolean>(true);
  const hasAutoSubmitted = useRef<boolean>(false);

  const coverageChangeTimeout = useRef<NodeJS.Timeout | null>(null);
  const isProcessingCoverageChange = useRef<boolean>(false);
  const lastCoverageState = useRef<string>('');

  const [residenceType, setResidenceType] = useState<DwellingType>(undefined);
  const [zipCode, setZipCode] = useState<string>('');
  const [includeSellersCoverage, setIncludeSellersCoverage] = useState<boolean>(false);

  const [fetchingZipState, setFetchingZipState] = useState<boolean>(false);

  const [residenceTypeError, setResidenceTypeError] = useState<string>(null);
  const [zipCodeError, setZipCodeError] = useState<string>(null);

  const [shareQuoteDrawerActive, setShareQuoteDrawerActive] = useState<boolean>(false);

  const [saveQuoteDrawerActive, setSaveQuoteDrawerActive] = useState<boolean>(false);

  const [notUpdatedCoverages, setNotUpdatedCoverages] = useState<ProductCoverage[]>([]);

  const feoptimizely = useOptimizelyFE();

  const [isPlanCoverageMFEEnabled, setIsPlanCoverageMFEEnabled] = useState(
    feoptimizely.isFeatureToggleOn(FEFeatures.PlanCoverageMFEEnabled),
  );

  const [tradeServiceFeeVariables, setTradeServiceFeeVariables] = useState(() =>
    feoptimizely.getFeatureVariables(FEFeatures.TradeServiceFeeEnabled, { brand }),
  );

  // Re-fetch when feoptimizely finishes initializing (handles hard refresh case)
  useEffect(() => {
    const variables = getTradeServiceFeeVariables(feoptimizely, brand);
    if (variables?.enabled !== tradeServiceFeeVariables?.enabled) {
      setTradeServiceFeeVariables(variables);
    }
    setIsPlanCoverageMFEEnabled(feoptimizely.isFeatureToggleOn(FEFeatures.PlanCoverageMFEEnabled));
  }, [feoptimizely]);

  const [serviceFees, setServiceFees] = useState<string[]>([]);

  // Default to Optimizely's default_service_fee while plans are loading,
  // will be overwritten with the intersected value once plans arrive
  const [selectedServiceFee, setSelectedServiceFee] = useState<number | null>(
    tradeServiceFeeVariables?.enabled && tradeServiceFeeVariables?.default_service_fee
      ? Number(tradeServiceFeeVariables.default_service_fee)
      : null,
  );

  const { addErrorToQueue } = useGlobalAlert();

  const getPendingFormData = (): PlansAndPricesFormData => ({
    residenceType,
    zipCode,
    includeSellersCoverage,
  });

  const isLoading = props.loadingPrice || !props.productPricingDetailsRequest;

  const handleMfeError = useCallback(
    (error) => {
      addErrorToQueue({
        id: 'product-addonlist-onStateChange-error',
        message: "'Failed onStateChange MFE Coverage'",
      });
      console.error('Failed onStateChange MFE Coverage', error);
    },
    [addErrorToQueue],
  );

  const handleOnStateChange = useCallback(
    (updatedState) => {
      if (!updatedState) return;

      const stateString = generateCoverageStateString(updatedState);

      if (stateString === lastCoverageState.current || isProcessingCoverageChange.current) {
        return;
      }

      if (coverageChangeTimeout.current) {
        clearTimeout(coverageChangeTimeout.current);
      }

      coverageChangeTimeout.current = setTimeout(() => {
        const changedItem = getChangedCoverageItem(updatedState);

        if (!changedItem) {
          return;
        }

        isProcessingCoverageChange.current = true;
        lastCoverageState.current = stateString;

        try {
          setNotUpdatedCoverages(cloneDeep(updatedState) || []);

          if (props.optionalCoverages.length > 0) {
            const selectedItem = props.optionalCoverages.find(
              (item) => item.id === changedItem.productCoverageID,
            );
            if (selectedItem) {
              props.onCoverageSelectionWithQuantity(selectedItem, changedItem.quantity);
            }
          }
        } finally {
          setTimeout(() => {
            isProcessingCoverageChange.current = false;
          }, 500);
        }
      }, 300);
    },
    [props.optionalCoverages, props.onCoverageSelectionWithQuantity],
  );

  function getChangedCoverageItem(updatedState) {
    if (!updatedState) {
      return null;
    }

    const isAnyCoverageSelected = updatedState.some((item) => item.quantity !== 0);

    // if the updated state is empty, or if there are no changes to the coverages, return null
    if (!notUpdatedCoverages?.length && !isAnyCoverageSelected) {
      return null;
    }

    return updatedState.find((updatedItem) => {
      const notUpdatedItem =
        notUpdatedCoverages.length !== 0
          ? notUpdatedCoverages.find(
              (item) => item.productCoverageID === updatedItem.productCoverageID,
            )
          : props.optionalCoverages.find((item) => item.id === updatedItem.productCoverageID);
      return JSON.stringify(notUpdatedItem) !== JSON.stringify(updatedItem);
    });
  }

  /** updates the state if the form data was changed */
  useEffect(() => {
    if (props.initialFormData) {
      setResidenceType(props.initialFormData.residenceType);
      setZipCode(props.initialFormData.zipCode);
      setIncludeSellersCoverage(props.initialFormData.includeSellersCoverage);
    }
  }, [props.initialFormData]);

  /** if zipCode is changed, then clear the error */
  useEffect(() => {
    setZipCodeError(null);

    // Auto-submit when ZIP code reaches 5 digits
    if (zipCode && zipCode.length === 5) {
      const autoSubmitZip = async () => {
        const zipError = await validateZipCode(zipCode);
        if (!zipError) {
          const updatedData = {
            residenceType,
            zipCode,
            includeSellersCoverage,
          };
          if (await validateForm(updatedData, false)) {
            props.onSubmit(updatedData);
            setInit(false);
          }
        } else {
          setZipCodeError(zipError);
        }
      };
      autoSubmitZip();
    }
  }, [zipCode]);

  /** if residence type is changed, then clear the error */
  useEffect(() => {
    setResidenceTypeError(null);
  }, [residenceType]);

  /** if there are pending form data changes, and form data is valid, then notify about it */
  useEffect(() => {
    validateForm(getPendingFormData(), false).then((valid) => {
      if (valid) {
        props.onFormDataChange?.(getPendingFormData());
        if (props.autoSubmitForm && !hasAutoSubmitted.current) {
          hasAutoSubmitted.current = true;
          validateAndSubmitForm(getPendingFormData());
        }
      }
    });
  }, [residenceType, zipCode, includeSellersCoverage]);

  /* if sellers coverage is disabled, exclude sellers coverage on UI */
  useEffect(() => {
    if (includeSellersCoverage && props.disableSellersCoverageOption) {
      setIncludeSellersCoverage(false);
    }
  }, [props.disableSellersCoverageOption]);

  /* Cleanup coverage timeout on unmount */
  useEffect(() => {
    return () => {
      if (coverageChangeTimeout.current) {
        clearTimeout(coverageChangeTimeout.current);
      }
    };
  }, []);

  const coverageDetails = useMemo(() => {
    let covDetails = [];
    if (props.optionalCoverages && props.optionalCoverages.length > 0) {
      covDetails = props.optionalCoverages.map((c) => ({
        productCoverageID: c?.id || '',
        promoted: false,
        price: c?.price || undefined,
        termSymbol: '',
      }));
    }

    return covDetails;
  }, [props.optionalCoverages]);

  const mfeState = useMemo(() => {
    return coverageDetails.map((coverage) => {
      const matchingCoverage = props.selectedCoverages.find(
        (selectedCoverage) => selectedCoverage.id === coverage.productCoverageID,
      );

      return {
        productCoverageID: coverage.productCoverageID,
        quantity: matchingCoverage ? 1 : 0,
      };
    });
  }, [coverageDetails, props.selectedCoverages]);

  // Sync MFE coverage state when plan changes to preserve selections
  useEffect(() => {
    if (mfeState?.length > 0) {
      const newStateString = generateCoverageStateString(mfeState);

      if (newStateString !== lastCoverageState.current && !isProcessingCoverageChange.current) {
        lastCoverageState.current = newStateString;
        setNotUpdatedCoverages(cloneDeep(mfeState));
      }
    }
  }, [mfeState]);

  const effectiveServiceFee = useMemo(() => {
    return tradeServiceFeeVariables?.enabled ? selectedServiceFee : props.tradeServiceFee;
  }, [selectedServiceFee, props.tradeServiceFee, tradeServiceFeeVariables?.enabled]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // prevent form submission
    await validateAndSubmitForm();
  };

  /** validate the form and submit if valid. by default, uses the state values. otherwise, values may be passed in directly. */
  const validateAndSubmitForm = async (
    data: PlansAndPricesFormData = getPendingFormData(),
  ): Promise<void> => {
    if (await validateForm(data)) {
      if (!init) {
        // only trigger on updates as this validateAndSubmitForm is currently called on init load as well
        fireGAEvent(PLAN_PRICE__UPDATE_PRICE(data));
      }

      props.onSubmit(data); // TODO: ARE-8966 -- ensure relevant prior work is canceled to avoid displaying inaccurate plans due to race conditions
      setInit(false);
    }
  };

  /** validate the form data and return true if no errors. by default, uses the state values. otherwise, values may be passed in directly. */
  const validateForm = async (
    data: PlansAndPricesFormData = getPendingFormData(),
    updateFormErrorStates = true,
  ): Promise<boolean> => {
    const errors = {
      residenceType: validateResidenceType(data.residenceType),
      zipCode: await validateZipCode(data.zipCode, updateFormErrorStates), // only validate exists when we want to update form error
    };

    if (updateFormErrorStates) {
      setResidenceTypeError(errors.residenceType);
      setZipCodeError(errors.zipCode);
    }

    return Object.keys(errors).every((k) => !errors[k]);
  };

  const validateResidenceType = (val: string): string => {
    if (!val) {
      return PLANS_AND_PRICES_FORM_ERROR.MISSING_RESIDENCE_TYPE;
    }
    return null;
  };

  /** validates the zip code is the correct length and if exists (has a US state) */
  const validateZipCode = async (val: string, validateExists = true): Promise<string> => {
    if (!val) {
      return PLANS_AND_PRICES_FORM_ERROR.INVALID_ZIP_CODE;
    }
    if (val.length !== 5) {
      return PLANS_AND_PRICES_FORM_ERROR.INVALID_ZIP_CODE;
    }

    if (validateExists) {
      const zipState = await validateZipExists(val);
      if (!zipState) {
        return PLANS_AND_PRICES_FORM_ERROR.INVALID_ZIP_CODE;
      }
    }

    return null;
  };

  /** sets and unsets the state that we are fetching the zip's state */
  const validateZipExists = (val: string): Promise<string> => {
    setFetchingZipState(true);
    return props
      .getZipCityState(val)
      .then((res) => {
        const state = res?.state;
        console.info(`zip=${val}, state=${state}`);
        return state;
      })
      .finally(() => {
        setFetchingZipState(false);
      });
  };

  const onSelectResidenceType = async (option: SelectOption) => {
    const newResidenceType = option?.value as DwellingType;
    setResidenceType(newResidenceType);

    // Auto-submit if form is valid
    const updatedData = {
      residenceType: newResidenceType,
      zipCode,
      includeSellersCoverage,
    };
    if (await validateForm(updatedData, false)) {
      props.onSubmit(updatedData);
      setInit(false);
    }
  };

  const onSelectSellersCoverage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSellersCoverage = e.target.checked;
    setIncludeSellersCoverage(newSellersCoverage);

    // Auto-submit if form is valid
    const updatedData = {
      residenceType,
      zipCode,
      includeSellersCoverage: newSellersCoverage,
    };
    if (await validateForm(updatedData, false)) {
      props.onSubmit(updatedData);
      setInit(false);
    }
  };

  const onBlurZIPInput = async () => {
    const zipError = await validateZipCode(zipCode);
    setZipCodeError(zipError);

    // Auto-submit if zip is valid and form is complete
    if (!zipError) {
      const updatedData = {
        residenceType,
        zipCode,
        includeSellersCoverage,
      };
      if (await validateForm(updatedData, false)) {
        props.onSubmit(updatedData);
        setInit(false);
      }
    }
  };

  /** two separate instances of this is used for mobile and for desktop view. */
  const QuoteSummary = () => {
    const onClickShareQuoteButton = () => {
      fireGAEvent(PLAN_PRICE__SHARE_QUOTE);
      setShareQuoteDrawerActive(true);
    };

    const onClickSaveQuoteButton = () => {
      setSaveQuoteDrawerActive(true);
    };

    return (
      <>
        {props.plans.length > 0 && (
          <CardQuoteSummary
            quoteSummary={props.quoteSummary}
            residenceType={props.formData?.residenceType}
            zipCode={props.formData?.zipCode}
            sellersCoverage={props.formData?.includeSellersCoverage}
            onClickShareQuoteButton={onClickShareQuoteButton}
            onClickSaveQuoteButton={onClickSaveQuoteButton}
            onClickStartOrderButton={onClickStartNewOrderButton}
            tradeServiceFee={selectedServiceFee}
            isLoading={isLoading}
          />
        )}
      </>
    );
  };

  const handleSelect = useCallback(
    (id: string) => {
      let planContent = props.plans.find((plan) => plan.productPlanId === id);
      if (tradeServiceFeeVariables?.enabled) {
        planContent = props.plans.find(
          (plan) => plan.productPlanId === id && plan.serviceFee === selectedServiceFee,
        );
      }
      props.onPlanSelection(planContent);
    },
    [props.plans, props.onPlanSelection, selectedServiceFee],
  );

  const resetOptions = () => {
    setResidenceType(props.initialFormData.residenceType);
    setZipCode(props.initialFormData.zipCode);
    setIncludeSellersCoverage(props.initialFormData.includeSellersCoverage);
    props.setSelectedPlan(null);
    props.setSelectedCoverageItems([]);
    if (tradeServiceFeeVariables?.enabled) {
      setSelectedServiceFee(Number(tradeServiceFeeVariables.default_service_fee));
    }
  };

  const onClickStartNewOrderButton = () => {
    // Validate required data before navigating
    if (!props.selectedPlan?.pvid || !props.productPricingDetailsRequest) {
      return;
    }

    // Find the correct plan based on selectedServiceFee if brand has service fee toggle
    let actualSelectedPlan = props.selectedPlan;
    if (tradeServiceFeeVariables?.enabled) {
      const planWithCorrectServiceFee = props.plans.find(
        (plan) =>
          plan.productPlanId === props.selectedPlan.productPlanId &&
          plan.serviceFee === selectedServiceFee,
      );
      if (planWithCorrectServiceFee) {
        actualSelectedPlan = planWithCorrectServiceFee;
      }
    }

    // Validate that we have a valid plan after service fee lookup
    if (!actualSelectedPlan?.pvid) {
      return;
    }

    const property = props.productPricingDetailsRequest.property;

    const newOrderData: NewOrderData = {
      propertyAddr: {
        zip: zipCode,
      },
      propertyDetails: {
        age: property?.age,
        newConstruction: isAgeNewConstruction(property?.age),
        notNewConstruction: !isAgeNewConstruction(property?.age),
        squareFootage: getSquareFootageRangeValue(property?.squareFootage),
        residenceType: dwellingTypeDictionary[residenceType],
        typeOfResidence: dwellingTypeDictionary[residenceType],
        propertyID: null,
        mlsNumber: '',
      },
    };

    const planAndCoverageData: PlansCoverageValues = {
      selectedFilters: {
        includeSellersCoverage,
        termYear: 1,
        includeACCoverage: true,
        selectedFee: actualSelectedPlan.serviceFee?.toString() || selectedServiceFee.toString(),
      },
      selectedPlanCoverages: {
        product: {
          starProductPlanID: actualSelectedPlan.productPlanId,
          starPVID: actualSelectedPlan.pvid,
          name: actualSelectedPlan.heading,
          contractTermMonths: 12,
          sellersCoverage: includeSellersCoverage,
          acCoverage: true,
          id: null,
        },
        optionalCoverages: (props.selectedCoverages || []).map((c) => ({
          id: c.id,
          name: c.name,
          quantity: 1,
        })),
        groupCoverages: [],
      },
      selectedGuestUnit: false,
      selectedMilitaryDiscount: false,
      selectedFirstResponderDiscount: false,
      selectedPromoCodeDiscount: false,
      selectedPromoCode: '',
    };
    planAndCoverageData.selectedGuestUnit = hasGuestUnitCoverage(
      planAndCoverageData.selectedPlanCoverages.optionalCoverages,
    );

    const routeData: NewOrderRouteData = {
      values: newOrderData,
      planCoverageValues: planAndCoverageData,
      source: NEW_ORDER_SOURCE_PLANS_AND_PRICES,
    };

    fireGAEvent(
      PLAN_PRICE__START_ORDER(RESIDENCE_DISPLAY[residenceType], zipCode, includeSellersCoverage),
    );
    navigate(Path.NewOrder, { state: routeData });
  };

  const zestyUrl = isProdEnv()
    ? 'https://productcoverage.zestyapi.frontdoorhome.com'
    : 'https://productcoverage-dev.zesty.dev';

  // Only populate serviceFees once both plans and tradeServiceFeeVariables are ready.
  // Show only fees that exist in both the product API response and Optimizely config.
  useEffect(() => {
    if (props.plans?.length > 0 && tradeServiceFeeVariables?.enabled) {
      const planFees = Array.from(new Set(props.plans.map((plan) => plan.serviceFee.toString())));
      const optimizelyFees = tradeServiceFeeVariables.available_service_fees
        ? tradeServiceFeeVariables.available_service_fees.split(',').map((f: string) => f.trim())
        : planFees; // if no Optimizely fees configured, use all plan fees

      const intersectedFees = planFees
        .filter((fee) => optimizelyFees.includes(fee))
        .sort((a, b) => Number(a) - Number(b));

      setServiceFees(intersectedFees);

      // Prefer default_service_fee from Optimizely if it exists in the intersected fees,
      // otherwise fall back to the first intersected fee
      const defaultFee = tradeServiceFeeVariables?.default_service_fee
        ? String(tradeServiceFeeVariables.default_service_fee)
        : null;
      const feeToSelect =
        defaultFee && intersectedFees.includes(defaultFee)
          ? Number(defaultFee)
          : intersectedFees[0]
            ? Number(intersectedFees[0])
            : null;
      setSelectedServiceFee(feeToSelect);
    }
  }, [props.plans?.length, tradeServiceFeeVariables?.enabled]);

  // Filter plans based on brand - for HBW210, only show plans with serviceFee 189
  const filteredPlans = useMemo(() => {
    if (!props.plans) return [];
    //Don't filter until tradeServiceFeeVariables and selectedServiceFee are both ready
    if (!tradeServiceFeeVariables) return [];
    if (tradeServiceFeeVariables.enabled && selectedServiceFee) {
      return props.plans.filter((plan) => plan.serviceFee === selectedServiceFee);
    }
    return props.plans;
  }, [props.plans, selectedServiceFee, tradeServiceFeeVariables]);

  const plansCardsData = useMemo(() => {
    if (filteredPlans.length > 0) {
      return filteredPlans.map((plan) => ({
        productPlanID: plan.productPlanId,
        promoted: false,
        regularPrice: plan.price.toString(),
        promotionalPrice: '',
        termSymbol: '',
      }));
    }
    return [];
  }, [filteredPlans]);

  return (
    <>
      <div className=" border-t border-b border-gray-300 pb-10">
        <ContentBoxContainer className="mx-auto py-2 flex flex-row-reverse">
          <div id="quote-summary-container" className="hidden xl:block">
            <div className="sticky-container-top w-80 ml-5">
              <QuoteSummary />
            </div>
          </div>
          <div id="result-container" className="flex-1 w-full">
            <div
              id="result-container--form"
              className={`${getCurrentThemeConfig().plansPricesBaseCoverageContainer} pt-8`}
            >
              <form>
                <REText variant="heading-04" className="mt-0" color="primary">
                  {PLANS_AND_PRICES_LABELS.FORM_HEADER}
                </REText>

                <Panel
                  rounded={getCurrentThemeConfig().plansPricesBaseCoverageRounded}
                  backgroundColor="white"
                  borderT={getCurrentThemeConfig().plansPricesBaseCoverageBorderT}
                  borderColor="gray-100"
                  shadow={false}
                  padding="xl"
                  className="flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0"
                  id="filter-content"
                >
                  <div className="w-full">
                    <SelectResidenceType
                      id="result-container--form--select-residence-type"
                      selected={residenceType}
                      onSelect={onSelectResidenceType}
                      disabled={props.loading}
                      error={residenceTypeError}
                    />
                  </div>
                  <div className="w-full">
                    <InputZipCode
                      id="result-container--form--input-zipcode"
                      value={zipCode}
                      onChangeZIP={(val) => setZipCode(val)}
                      onBlur={onBlurZIPInput}
                      disabled={props.loading}
                      error={zipCodeError}
                      loading={fetchingZipState}
                    />
                  </div>
                  <div className="w-full">
                    <div className="flex flex-col space-y-1">
                      <div className="flex flex-row space-x-2 items-center">
                        <REText variant="label">
                          {PLANS_AND_PRICES_LABELS.SELLERS_COVERAGE_INPUT}
                        </REText>
                        <Popover
                          placement="top"
                          triggerInteraction="hover"
                          content={(popoverContentProps) => (
                            <PopoverContent {...popoverContentProps}>
                              <REText className="max-w-xs" variant="caption">
                                {props.disableSellersCoverageOption
                                  ? PLANS_AND_PRICES_LABELS.SELLERS_COVERAGE_RESTRICTED_TOOLTIP
                                  : PLANS_AND_PRICES_LABELS.SELLERS_COVERAGE_TOOLTIP}
                              </REText>
                            </PopoverContent>
                          )}
                        >
                          <IconInfoFull
                            color="interactive"
                            size={18}
                            className={classNames(['inline'])}
                          />
                        </Popover>
                      </div>
                      <Switch
                        id="result-container--form--checkbox-sellers-coverage"
                        value="yes"
                        on={includeSellersCoverage}
                        onChange={onSelectSellersCoverage}
                        disabled={
                          props.loading ||
                          props.loadingFilters ||
                          props.disableSellersCoverageOption
                        }
                      />
                    </div>
                  </div>
                  {tradeServiceFeeVariables?.enabled && (
                    <div className="w-full tsfMain">
                      <TradeServiceFee
                        selectedFee={selectedServiceFee?.toString()}
                        serviceFees={serviceFees}
                        onChange={(fee) => {
                          const newServiceFee = Number(serviceFees[fee]);
                          setSelectedServiceFee(newServiceFee);
                          props.onServiceFeeChange(newServiceFee);
                        }}
                      />
                    </div>
                  )}
                </Panel>
                <div className="mt-1">
                  <Button
                    id="result-container--form--submit-btn"
                    label={PLANS_AND_PRICES_LABELS.SUBMIT_BTN}
                    size="medium"
                    width="full"
                    onClick={onSubmit}
                    disabled={props.loading}
                    loading={props.loading}
                    loadingAnimation="spinner-only"
                    className={classNames([props.autoSubmitForm && 'hidden'])}
                  />
                </div>
              </form>
            </div>
            <div
              id="result-container--plans"
              className={
                isPlanCoverageMFEEnabled || getBrandConstants().plansPricesDisplayMFE
                  ? 'bg-white rounded-5'
                  : ''
              }
            >
              {!props.loading &&
                !props.loadingPageMeta &&
                !props.loadingPrice &&
                filteredPlans.length === 0 &&
                (!tradeServiceFeeVariables?.enabled || !!selectedServiceFee) && (
                  <EmptyResultsContainer init={init} />
                )}

              {!props.loading && filteredPlans.length > 0 && props.selectedPlan?.productPlanId && (
                <div className="product-comparison-cards-micro-frontend">
                  {isPlanCoverageMFEEnabled || getBrandConstants().plansPricesDisplayMFE ? (
                    <ProductComparisonCardsMicroFrontend
                      tenant={getCurrentThemeConfig().plansPricesMFETenant as Tenant}
                      theme={getCurrentThemeConfig().plansPricesMFETheme as Theme}
                      cmsBaseURL={zestyUrl}
                      language="en"
                      planTitleAlignment="left"
                      productDetailsList={plansCardsData}
                      showIncludedCoverageLevels
                      showExcludedPlanHighlights={false}
                      showPlanIcons={false}
                      onProductPlanSelect={handleSelect}
                      onError={(error) => {
                        addErrorToQueue({
                          id: 'product-coverages-onSelectPlan-error',
                          message: 'Failed on select Plan',
                        });
                        console.error('Failed on select Plan', error);
                      }}
                      initialSelectedPlan={props.selectedPlan?.productPlanId}
                    />
                  ) : (
                    <PlanCardsOld
                      plans={filteredPlans}
                      selectedPlan={props.selectedPlan}
                      onPlanSelection={props.onPlanSelection}
                      meta={props.meta}
                      loading={props.loading}
                      loadingPageMeta={props.loadingPageMeta}
                      getIncludedCoverages={props.getIncludedCoverages}
                      init={init}
                    />
                  )}
                </div>
              )}
            </div>
            {filteredPlans.length > 0 && (
              <div id="result-container--footer" className="mt-6 space-y-4">
                {!props.loadingPageMeta && props.meta?.footnote && (
                  <div id="result-container--footer--footnote">
                    <REText
                      className="zesty-html"
                      dangerouslySetInnerHTML={{ __html: props.meta?.footnote }}
                    />
                  </div>
                )}

                <PlansPriceTradeServiceFeeFooter
                  loadingServiceFee={props.loading}
                  loadingText={props.loadingPageMeta}
                  serviceFee={effectiveServiceFee}
                  serviceFeeDescription={props.meta?.tscf_text}
                  serviceFeeTooltip={props.meta?.tscf_tooltip}
                />
              </div>
            )}

            {filteredPlans.length > 0 && (
              <div className="border-t-1 border-gray-300 mt-5 py-3 space-y-2">
                <div className={isLoading ? 'pointer-events-none opacity-50' : ''}>
                  {isPlanCoverageMFEEnabled || getBrandConstants().plansPricesDisplayMFE ? (
                    <Panel
                      rounded={getCurrentThemeConfig().plansPricesCoverageRounded}
                      backgroundColor="white"
                      borderT={getCurrentThemeConfig().plansPricesCoverageBorderT}
                      borderColor="gray-100"
                      shadow={false}
                      className="flex flex-col product-add-on-selection-list-micro-frontend px-0 md:p-4"
                      id="plans-optional-coverages"
                    >
                      <REText variant="heading-04" className="mt-5 mb-4" color="primary">
                        {PLANS_AND_PRICES_LABELS.COVERAGE_SELECTION_HEADER}
                      </REText>
                      <ProductAddOnSelectionListMicroFrontend
                        showCoverageIcon={true}
                        cmsBaseURL={zestyUrl}
                        coverageDetailsList={coverageDetails}
                        state={mfeState}
                        language="en"
                        onError={handleMfeError}
                        productPlanID={props.selectedPlan?.productPlanId}
                        onStateChange={handleOnStateChange}
                        tenant={getCurrentThemeConfig().plansPricesMFETenant as Tenant}
                        backgroundColor={getCurrentThemeConfig().plansPricesMFEBackgroundColor}
                      />
                    </Panel>
                  ) : (
                    <ProductAddOnOld
                      optionalCoverages={props.optionalCoverages}
                      selectedCoverages={props.selectedCoverages}
                      onCoverageSelection={props.onCoverageSelection}
                    />
                  )}
                </div>
                <div id="quote-summary-container-mobile" className="block xl:hidden">
                  <QuoteSummary />
                </div>
              </div>
            )}
          </div>
        </ContentBoxContainer>
      </div>

      <DrawerPlanPriceShareQuote
        isActive={shareQuoteDrawerActive}
        onClose={() => setShareQuoteDrawerActive(false)}
        onSuccess={resetOptions}
        quote={props.quoteSummary}
        productPricingDetailsRequest={props.productPricingDetailsRequest}
        formData={props.formData}
        cityOptions={props.cityOptions}
        tradeServiceFee={props.tradeServiceFee}
      />
      <DrawerPlanPriceSaveQuote
        isActive={saveQuoteDrawerActive}
        onClose={() => setSaveQuoteDrawerActive(false)}
        onSuccess={resetOptions}
        productPricingDetailsRequest={props.productPricingDetailsRequest}
        quote={props.quoteSummary}
        formData={props.formData}
        cityOptions={props.cityOptions}
        tradeServiceFee={props.tradeServiceFee}
      />
    </>
  );
};

export const EmptyResultsContainer: React.FC<{ init: boolean }> = ({ init }) => {
  const iconClassName = classNames(['text-gray-400']);


  return (
    <div className="min-h-96 flex flex-col items-center m-auto place-content-center max-w-96 text-center sm-max:p-8 py-20">
      {init && <IconPinSolid color="gray" size={48} className={iconClassName} />}

      <REText variant="heading-04" color="primary" id="no-plans--header" className="my-4">
        {init
          ? PLANS_AND_PRICES_LABELS.INIT_RESULT_HEADER
          : PLANS_AND_PRICES_LABELS.NO_RESULT_HEADER}
      </REText>

      <REText color="gray" id="no-plans--subtext" className="leading-snug text-gray-600">
        {init ? PLANS_AND_PRICES_LABELS.INIT_RESULT_SUBTEXT : <ServiceUnavailable />}
      </REText>
    </div>
  );
};

interface SelectResidenceTypeProps {
  id: string;
  selected?: DwellingType;
  disabled?: boolean;
  onSelect: SelectHandler<SelectOption>;
  error?: string;
}

const SelectResidenceType: React.FC<SelectResidenceTypeProps> = (props) => {
  const residenceTypeOptions: SelectOption[] = [
    {
      id: 'residence-type-option--sfr',
      label: 'Single Family Residence',
      value: DwellingType.SingleFamilyResidence,
    },
    {
      id: 'residence-type-option--condo',
      label: 'Condominium',
      value: DwellingType.Condominium,
    },
    {
      id: 'residence-type-option--th',
      label: 'Townhouse',
      value: DwellingType.Townhouse,
    },
    {
      id: 'residence-type-option--duplex',
      label: 'Duplex',
      value: DwellingType.Duplex,
    },
    {
      id: 'residence-type-option--triplex',
      label: 'Triplex',
      value: DwellingType.Triplex,
    },
    {
      id: 'residence-type-option--fourplex',
      label: 'Fourplex',
      value: DwellingType.Fourplex,
    },
    {
      id: 'residence-type-option--mh',
      label: 'Mobile Home',
      value: DwellingType.MobileHome,
    },
  ];

  return (
    <Select
      id={props.id}
      required={true}
      label={PLANS_AND_PRICES_LABELS.RESIDENCE_TYPE_INPUT}
      formField={true}
      options={residenceTypeOptions}
      onSelect={props.onSelect}
      selected={props.selected && residenceTypeOptions.find((o) => o.value === props.selected)}
      disabled={props.disabled}
      error={props.error}
    />
  );
};

interface InputZipCodeProps {
  id: string;
  value: string;
  onChangeZIP?: (zip: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  /** adds a spinner to the end of the input */
  loading?: boolean;
}

const InputZipCode: React.FC<InputZipCodeProps> = (props) => {
  const [value, setValue] = useState<string>('');

  /** update the state if prop value changes */
  useEffect(() => {
    setValue(props.value || '');
  }, [props.value]);

  return (
    <MaskedInput
      id={props.id}
      type="text"
      inputMode="numeric"
      required={true}
      label={PLANS_AND_PRICES_LABELS.ZIP_INPUT}
      formField={true}
      value={value}
      onChange={(_, val) => props.onChangeZIP(val)}
      onBlur={props.onBlur}
      maxLength={5}
      disabled={props.disabled}
      error={props.error}
      mask="00000"
      endEnhancer={
        props.loading && (
          <ProgressIndicator
            variant="circular"
            color="gray"
            size="small"
            value={undefined}
            showValueLabel={false}
          />
        )
      }
    />
  );
};
